import { ReactNode } from 'react';

import { Box } from 'components/Box';
import { ListingCounts } from 'components/Listing/ListingCounts';
import { CmsApiPage } from 'modules/cms/api/types/CmsApiPage';
import {
  getContentFirstRealBlockIndex,
  getContentHasBodyHeader,
  getContentListingCounts,
  getContentShowListingCounts,
} from 'modules/cms/helpers';
import { mapCmsBlocksForBody } from 'modules/cms/mappers';
import { sectionPadding } from 'theme';

import { CmsBodyContainer, CmsBodyContainerWidth } from './CmsBodyContainer';
import { CmsBodyHeader } from './CmsBodyHeader';
import { CmsBodyMainContent } from './CmsBodyMainContent';

type Props = {
  content: CmsApiPage;
  headerImage: ReactNode | undefined;
  padBottom: boolean;
  padTop: boolean;
  socialButtons: ReactNode | null | undefined;
  subscribeButton: ReactNode | null | undefined;
};

export function CmsBody({
  content,
  headerImage,
  padBottom,
  padTop,
  socialButtons,
  subscribeButton,
}: Props) {
  const showListingCounts = getContentShowListingCounts({ content });
  const listingCounts = getContentListingCounts({ content });
  const hasHeader = getContentHasBodyHeader({ content });
  const firstRealBlockIndex = getContentFirstRealBlockIndex({ content });

  const mappedBlocks = mapCmsBlocksForBody({ content });

  const firstRealBlock = mappedBlocks[firstRealBlockIndex];
  const firstRealBlockHasBackground = Boolean(
    mappedBlocks?.length && firstRealBlock?.hasBackground,
  );

  const headerContainerWidth =
    content.layout === 'NARROW'
      ? CmsBodyContainerWidth.NARROW_LAYOUT
      : CmsBodyContainerWidth.PAGE_LAYOUT;

  return (
    <Box
      component="main"
      ml="auto"
      mr="auto"
      mb={!padBottom ? [-36, null, null, -54] : undefined}
      data-qa-id="content-container"
    >
      {(hasHeader || headerImage || showListingCounts) && (
        <header>
          {hasHeader && (
            <Box m={sectionPadding}>
              <CmsBodyContainer $width={headerContainerWidth}>
                <CmsBodyHeader content={content} />
              </CmsBodyContainer>
            </Box>
          )}

          {headerImage && (
            <Box m={sectionPadding}>
              <CmsBodyContainer $width={headerContainerWidth}>
                {headerImage}
              </CmsBodyContainer>
            </Box>
          )}

          {showListingCounts && listingCounts ? (
            <ListingCounts
              darkMode={firstRealBlockHasBackground}
              listingCounts={listingCounts}
            />
          ) : null}
        </header>
      )}

      <Box
        marginLeft={sectionPadding}
        marginRight={sectionPadding}
        marginTop={padTop ? sectionPadding : undefined}
      >
        <CmsBodyMainContent
          content={content}
          mappedBlocks={mappedBlocks}
          socialButtons={socialButtons}
          subscribeButton={subscribeButton}
        />
      </Box>
    </Box>
  );
}
